// Ampliación de baterías: la hoja 2 OPCIONAL del formato piezas (Diego, 27-oct-2026; caso Jorge Rodríguez, SX-AMP-261023:
// «Con 8 baterías llegas al amanecer»). Explica al cliente, con una noche MEDIDA en su monitoreo, cuánto le ayudan las
// baterías nuevas. La cuenta, los textos y los candados son de la PLANTILLA (formatos/piezas/ampliacion.mjs de la
// -6j-ampliacion): aquí solo se CAPTURA lo medido, se arma el campo `servicio.ampliacion_baterias` y se revisa ANTES de
// generar lo que el vendedor puede arreglar (horas cruzadas, una noche que no empezó llena, más baterías de las que la ficha
// deja en paralelo…), con un mensaje que entiende. Si algo se escapa, la plantilla truena igual: nunca un PDF que prometa de más.
// Isomorfo (lo importan la web y el servidor): sin node:*.
import { z } from 'zod';
import { MSI_TOPE_LISTA_CON_IVA } from '../constantes.ts';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const FECHA = /^20\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
/** «con los aires prendidos», «el monitoreo de tu sistema Fortress»: minúscula al inicio, sin punto final (va a media frase). */
const MEDIA_FRASE = /^[a-záéíóúñü0-9][^.;:]*[^.;:,\s]$/;

const Hora = z.string().regex(HHMM, 'Escribe la hora en 24 h, HH:MM (por ejemplo 04:33 o 16:52).');
const FechaMedida = z.string().regex(FECHA, 'Escribe la fecha como AAAA-MM-DD.');
const MediaFrase = (min: number, max: number, ejemplo: string) =>
  z.string().min(min).max(max).regex(MEDIA_FRASE, `Va a media frase: empieza con minúscula y sin punto final (${ejemplo}).`);

/** La noche medida en el monitoreo en que el banco llegó al corte (la que se apagó). */
export const NocheMedida = z.strictObject({
  /** Fecha de la TARDE en que empezó esa noche. */
  fecha: FechaMedida,
  /** Qué estaba prendido, a media frase: «con los aires prendidos». */
  como: MediaFrase(3, 40, '«con los aires prendidos»'),
  /** Hora en que el banco empezó a bajar (los paneles dejaron de cubrir la casa). */
  inicio: Hora,
  /** Carga del banco a esa hora: lleno (95 % o más) o la cuenta no vale. */
  inicio_pct: z.int().min(1).max(100),
  /** Hora en que llegó al corte y la casa se quedó sin luz. */
  corte: Hora,
  /** Hora de la mañana en que los paneles ya dan más que la casa. */
  sol: Hora,
});
export type NocheMedida = z.infer<typeof NocheMedida>;

/** La recarga MEDIDA del día de la noche (o el siguiente): sin ella la hoja no dice «llegas al amanecer». */
export const RecargaMedida = z.strictObject({
  /** Hora en que el banco llegó a 100 %. */
  llenas_a_las: Hora,
  fecha: FechaMedida,
  /** kW con los que cargaban las baterías la última hora antes de llenarse. */
  carga_kw: z.number().positive().max(30),
});
export type RecargaMedida = z.infer<typeof RecargaMedida>;

/** Lo que el vendedor captura para la hoja 2. Las baterías NUEVAS no van aquí: salen de la partida de baterías. */
export const CapturaAmpliacionBaterias = z.strictObject({
  /** Baterías que el cliente ya tiene (del mismo modelo que las nuevas). */
  actuales: z.int().min(1).max(64),
  /** kWh nominales de cada batería: los mismos que dice el concepto de la partida (de la ficha). */
  kwh_por_bateria: z.number().positive().max(60),
  /** Máximo de baterías en paralelo según la ficha (eFlex MAX: 16). */
  max_en_paralelo: z.int().min(2).max(64),
  /** Carga a la que el inversor corta la descarga, como está configurado HOY. */
  corte_pct: z.int().min(5).max(60),
  /** Reserva que recomienda el fabricante para el uso diario (Fortress: 20 %). null = no se sabe (la hoja no la menciona). */
  reserva_fabricante_pct: z.int().min(5).max(60).nullable(),
  noche: NocheMedida,
  /** En una noche normal (sin los equipos de la noche medida) usa menos de este % del banco. */
  noche_normal_max_pct: z.int().min(1).max(100),
  recarga: RecargaMedida.nullable(),
  /** Los paneles que ya tiene (la fila «tu sistema» de la hoja). null = no se imprimen. */
  paneles: z.strictObject({ cantidad: z.int().min(1).max(400), w: z.int().min(100).max(800) }).nullable(),
  /** De dónde salen las horas, a media frase: «el monitoreo de tu sistema Fortress». */
  fuente: MediaFrase(5, 60, '«el monitoreo de tu sistema Fortress»'),
});
export type CapturaAmpliacionBaterias = z.infer<typeof CapturaAmpliacionBaterias>;

/** El campo `servicio.ampliacion_baterias` del JSON de piezas (subesquema de la plantilla -6j-ampliacion). */
export interface AmpliacionBateriasV1 {
  actuales: number;
  kwh_por_bateria: number;
  max_en_paralelo: number;
  corte_pct: number;
  reserva_fabricante_pct?: number;
  noche: NocheMedida;
  noche_normal_max_pct: number;
  recarga?: RecargaMedida;
  paneles?: { cantidad: number; w: number };
  fuente: string;
}

/** Arma el campo para la plantilla. Lo opcional que falta NO se manda (el subesquema no admite null en esos campos). */
export function ampliacionBateriasV1(c: CapturaAmpliacionBaterias): AmpliacionBateriasV1 {
  return {
    actuales: c.actuales,
    kwh_por_bateria: c.kwh_por_bateria,
    max_en_paralelo: c.max_en_paralelo,
    corte_pct: c.corte_pct,
    ...(c.reserva_fabricante_pct != null ? { reserva_fabricante_pct: c.reserva_fabricante_pct } : {}),
    noche: { fecha: c.noche.fecha, como: c.noche.como, inicio: c.noche.inicio, inicio_pct: c.noche.inicio_pct, corte: c.noche.corte, sol: c.noche.sol },
    noche_normal_max_pct: c.noche_normal_max_pct,
    ...(c.recarga ? { recarga: { llenas_a_las: c.recarga.llenas_a_las, fecha: c.recarga.fecha, carga_kw: c.recarga.carga_kw } } : {}),
    ...(c.paneles ? { paneles: { cantidad: c.paneles.cantidad, w: c.paneles.w } } : {}),
    fuente: c.fuente,
  };
}

// ───────────────────────────── Lo que se precarga de la ficha de la batería ─────────────────────────────

/** Reserva para el USO DIARIO que recomienda el fabricante, MEDIDA en su manual. Solo con esta cifra la hoja dice «es la
 *  reserva que recomienda el fabricante». Una batería que no está aquí sale con null: el vendedor la teclea si la tiene
 *  del manual, o la hoja no la menciona. */
export const RESERVAS_FABRICANTE: readonly { marca: RegExp; modelo: RegExp; pct: number; fuente: string }[] = Object.freeze([
  { marca: /^fortress$/i, modelo: /eflex\s*max/i, pct: 20, fuente: 'Manual Fortress eFlex MAX §9.8: 20 % de reserva para el uso diario (caso SX-AMP-261023, 27-oct-2026).' },
]);

/** Lo mínimo de la ficha que la hoja 2 necesita (la tabla de precios o el catálogo del motor lo traen). */
export interface FichaBateriaAmpliacion {
  marca: string;
  modelo: string;
  kwh_nominales_por_modulo: number;
  modulos_max: number;
}

/** kWh de la batería tal como los dice su nombre comercial (5.374 → 5.4): el concepto de la partida y la hoja usan el mismo. */
export const kwhComercial = (kwhNominales: number): number => Math.round(kwhNominales * 10) / 10;

/** Lo que se precarga de la ficha: kWh, máximo en paralelo, reserva del fabricante y de dónde salen las horas. */
export function precargaDeFicha(f: FichaBateriaAmpliacion): Pick<CapturaAmpliacionBaterias, 'kwh_por_bateria' | 'max_en_paralelo' | 'reserva_fabricante_pct' | 'fuente'> {
  return {
    kwh_por_bateria: kwhComercial(f.kwh_nominales_por_modulo),
    max_en_paralelo: f.modulos_max,
    reserva_fabricante_pct: reservaDeFicha(f),
    fuente: `el monitoreo de tu sistema ${f.marca.trim()}`.slice(0, 60),
  };
}

const num1 = (n: number) => {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

/** El modelo sin los kWh del final («eFlex MAX 5.4» → «eFlex MAX»): en la descripción los kWh van en «de X a Y kWh». */
const modeloSinKwh = (modelo: string) => modelo.replace(/\s+\d+(?:\.\d+)?\s*(?:kwh)?$/i, '').trim();

/** Concepto de la partida de baterías (46 caracteres como máximo) con sus kWh: la plantilla exige que traiga los mismos
 *  kWh que `kwh_por_bateria` («Batería Fortress eFlex MAX 5.4 kWh»). Si no cabe, primero se quita «Batería» y no la marca
 *  (verificación de ventas del 27-oct: «Batería Fidus Battery Plus FB-L-16 16.1 kWh» perdía «Pylontech» frente al cliente). */
export function conceptoBateria(f: Pick<FichaBateriaAmpliacion, 'marca' | 'modelo'>, kwh: number): string {
  const k = `${num1(kwh)} kWh`;
  const modelo = f.modelo.trim();
  const marca = f.marca.trim();
  const conKwh = new RegExp(`(^|[^\\d.])${num1(kwh).replace('.', '\\.')}$`).test(modelo) ? `${modelo} kWh` : `${modelo} ${k}`;
  for (const c of [`Batería ${marca} ${conKwh}`, `${marca} ${conKwh}`, `Batería ${conKwh}`, conKwh]) if (c.length <= 46) return c;
  return conKwh.slice(0, 46);
}

/** La frase de la descripción que apunta a la hoja 2: el puente del PDF la quita si la plantilla no trae la hoja 2. */
export const FRASE_HOJA_2_AMPLIACION = ' Hoja 2: cómo te ayudan.';

/** Descripción de la hoja 1 (150 caracteres como máximo). La plantilla revisa que cada número junto a «baterías» o «kWh»
 *  sea de la cuenta («de 6 a 8», «de 32.4 a 43.2 kWh», «Sumamos 2»): aquí salen de la misma cuenta, nunca a mano. */
export function descripcionAmpliacion(a: { marca: string; modelo: string; actuales: number; nuevas: number; kwh: number; conHoja2: boolean }): string {
  const tus = a.actuales === 1 ? 'a tu batería' : `a tus ${a.actuales} baterías`;
  const base = `Sumamos ${a.nuevas} ${a.marca.trim()} ${modeloSinKwh(a.modelo)} ${tus}: de ${num1(a.actuales * a.kwh)} a ${num1((a.actuales + a.nuevas) * a.kwh)} kWh.`;
  const hoja2 = a.conHoja2 ? FRASE_HOJA_2_AMPLIACION : '';
  const corta = `Sumamos ${a.nuevas} baterías ${tus}: de ${num1(a.actuales * a.kwh)} a ${num1((a.actuales + a.nuevas) * a.kwh)} kWh.`;
  for (const d of [base + hoja2, corta + hoja2, corta]) if (d.length <= 150) return d;
  return corta.slice(0, 150);
}

// ───────────────────────────── Revisión antes de generar (la plantilla es el juez final) ─────────────────────────────

const DIA = 1440;
const aMin = (t: string) => {
  const m = HHMM.exec(t);
  return m ? Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) : NaN;
};
const tras = (de: number, a: number) => (((a - de) % DIA) + DIA) % DIA;
const fechaReal = (f: string) => {
  const d = new Date(`${f}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === f ? d.getTime() : null;
};
const hm = (min: number) => `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`;

/** Lo mismo que exigen los candados de la plantilla sobre lo CAPTURADO (formatos/piezas/ampliacion.mjs, 27-oct-2026), dicho
 *  para el vendedor y antes de generar. `nuevas` = cantidad de la partida de baterías; `fecha` = la de la cotización. */
export function revisarAmpliacionBaterias(c: CapturaAmpliacionBaterias, o: { nuevas: number; fecha: string }): string[] {
  const e: string[] = [];
  const total = c.actuales + o.nuevas;
  if (!Number.isInteger(o.nuevas) || o.nuevas < 1) e.push('La hoja 2 necesita la partida de baterías con 1 o más baterías enteras (son las que se suman).');
  if (total > c.max_en_paralelo) e.push(`Con ${c.actuales} que ya tiene y ${o.nuevas} nuevas son ${total} baterías: la ficha permite ${c.max_en_paralelo} en paralelo.`);
  const n = c.noche;
  const ini = aMin(n.inicio), corte = aMin(n.corte), sol = aMin(n.sol);
  if (![ini, corte, sol].some(Number.isNaN)) {
    const hoy = tras(ini, corte), alSol = tras(ini, sol);
    if (hoy < 120 || hoy > 16 * 60) e.push(`De las ${n.inicio} a las ${n.corte} son ${hm(hoy)}: una noche medida va de 2 a 16 horas. ¿Se cruzaron las horas?`);
    else if (alSol <= hoy) e.push(`Hoy se apagan a las ${n.corte} y los paneles cubren la casa a las ${n.sol}: el sol tiene que llegar DESPUÉS del corte. Revisa las horas.`);
    else if (alSol > 16 * 60) e.push(`De las ${n.inicio} a las ${n.sol} son ${hm(alSol)}: más de 16 horas. ¿Se cruzaron las horas?`);
    else {
      const kw = (c.actuales * c.kwh_por_bateria * Math.max(0, n.inicio_pct - c.corte_pct)) / 100 / (hoy / 60);
      if (kw > 8) e.push(`Con esas horas la casa habría usado ${kw.toFixed(1)} kW en promedio toda la noche: ninguna casa promedia más de 8 kW. Revisa las horas y el % de inicio.`);
      if (n.inicio_pct > c.corte_pct && Math.floor((hoy * total) / c.actuales - hoy) < 10) e.push('Las baterías nuevas darían menos de 10 minutos más esa noche: la hoja 2 no tendría nada que decir.');
      const alCorte = Math.floor(n.inicio_pct - ((n.inicio_pct - c.corte_pct) * c.actuales) / total);
      if (n.inicio_pct > c.corte_pct && o.nuevas >= 1 && alCorte <= c.corte_pct) e.push(`Con ${total} baterías, a las ${n.corte} quedaría ${alCorte} %: no pasa del corte (${c.corte_pct} %).`);
    }
    if (c.recarga) {
      const llenas = aMin(c.recarga.llenas_a_las);
      if (!Number.isNaN(llenas) && !(tras(sol, llenas) >= 60 && tras(sol, llenas) < tras(sol, ini)))
        e.push(`El banco se llenó a las ${c.recarga.llenas_a_las}: debe ser por lo menos 1 h después de que entran los paneles (${n.sol}) y antes de que empiece la noche (${n.inicio}). ¿Es de madrugada por error (02:40 en vez de 14:40)?`);
    }
  }
  const fNoche = fechaReal(n.fecha);
  const fRecarga = c.recarga ? fechaReal(c.recarga.fecha) : null;
  if (fNoche === null) e.push(`La fecha de la noche (${n.fecha}) no existe en el calendario.`);
  if (c.recarga && fRecarga === null) e.push(`La fecha de la recarga (${c.recarga.fecha}) no existe en el calendario.`);
  const fc = fechaReal(o.fecha);
  if (fc !== null)
    for (const [t, que] of [[fNoche, 'de la noche'], [fRecarga, 'de la recarga']] as const)
      if (t !== null && (t > fc || t < fc - 400 * 864e5)) e.push(`La fecha ${que} debe ser de los 13 meses antes de la cotización (${o.fecha}), no futura ni más vieja.`);
  // verificación del 27-oct: la cuenta supone que la recarga arranca del corte, y eso solo es seguro la mañana SIGUIENTE a la noche
  // medida (la del mismo día empezó de donde amaneció el banco, que no se mide): la plantilla -v4 lo exige igual.
  if (fNoche !== null && fRecarga !== null && Math.round((fRecarga - fNoche) / 864e5) !== 1) e.push('La recarga es la del día SIGUIENTE a la noche medida (esa mañana el banco arranca del corte): de otro día no dice cuánto tarda en llenar las nuevas.');
  if (n.inicio_pct < 95) e.push(`La noche empezó en ${n.inicio_pct} %. La hoja pide una noche que empezó con el banco lleno (95 % o más): si el sol no lo llenó, las baterías nuevas tampoco se llenan. Usa otra noche del monitoreo.`);
  if (n.inicio_pct <= c.corte_pct) e.push(`La noche empezó en ${n.inicio_pct} % y el corte es ${c.corte_pct} %: no hay energía que contar.`);
  else if (c.noche_normal_max_pct >= n.inicio_pct - c.corte_pct) e.push(`«En una noche normal usa menos de ${c.noche_normal_max_pct} %» no cabe entre ${n.inicio_pct} % y el corte de ${c.corte_pct} %.`);
  return e;
}

// ═════════════════════════════ La cotización de AMPLIACIÓN DE BATERÍAS (tipo «ampliacion_baterias», formato piezas) ═════════════════════════════
// Diego, 27-oct-2026 (respuesta a la sesión del cotizador): «Tipo completo» con la hoja 2 opcional, y el precio «como el de
// Jorge pero agrega comisión al vendedor y el porcentaje de error por si pasa algo; en todas, aunque sea cotización mía,
// lleva comisión siempre». ADENDA-DIEGO línea 90 (equipos sueltos): contado sin IVA = costo ÷ (1 − 30 % − comisión); línea
// 212: imprevistos 2 %. Aquí: contado sin IVA = costo ÷ (1 − margen − comisión − imprevistos), en cada partida (baterías,
// material e instalación con traslado), redondeado HACIA ARRIBA a los $100 (nunca debajo de la regla). La lista (3 MSI) la
// calcula la plantilla: contado ÷ 0.85. Lo que el vendedor teclea son COSTOS; el precio no se teclea.

/** Margen que queda libre después de comisión e imprevistos en equipos sueltos (ADENDA-DIEGO línea 90). */
export const MARGEN_EQUIPOS_SUELTOS_PCT = 30;
/** Diego, 27-oct: «aunque sea cotización mía, lleva comisión siempre». Quien no tiene comisión (el director) paga la de un
 *  asesor interno (6 %, COMISION_POR_ROL.asesor). */
export const COMISION_MINIMA_AMPLIACION_PCT = 6;
/** IVA de la plantilla de piezas (precios con IVA del 16 %, adenda A0b). */
const IVA_PIEZAS = 0.16;
/** Los precios unitarios se redondean HACIA ARRIBA a esto (el margen nunca queda debajo de la regla). */
export const REDONDEO_PU_AMPLIACION = 100;

/** La batería elegida (un renglón de la tabla de precios, o del catálogo del motor si la tabla no la trae). */
export const BateriaAmpliacion = z.strictObject({
  /** id del renglón de la tabla de precios (null = del catálogo del motor: su costo lo teclea el vendedor). */
  id_tabla: z.string().max(40).nullable(),
  marca: z.string().trim().min(2).max(30),
  modelo: z.string().trim().min(1).max(40),
  /** kWh de un módulo real (0.5 a 100): con menos, «5.4 kWh» se redondea a 0 y la hoja 2 no tiene cuenta (verificación del 27-oct). */
  kwh_nominales_por_modulo: z.number().min(0.5).max(100),
  modulos_max: z.int().min(2).max(64),
  garantia_anios: z.int().min(1).max(30),
});
export type BateriaAmpliacion = z.infer<typeof BateriaAmpliacion>;

/** De dónde sale el costo de cada batería: el de la tabla de precios de Diego (el vendedor NO lo ve: lo pone el servidor al
 *  calcular, y la vista lo quita a quien no es director), o el que el vendedor teclea de un proveedor, con su fuente
 *  («Infinity Energy S01401, USD 1,529.20 × 19.227»). El tecleado deja un aviso amarillo y va a la bitácora de Diego. */
export const CostoBateriaAmpliacion = z.discriminatedUnion('origen', [
  z.strictObject({ origen: z.literal('tabla') }),
  /** `para` = «marca modelo» de la batería cotizada con el proveedor. Si el vendedor cambia de batería, el costo de la anterior ya no
   *  vale: rojo hasta que teclee el de la nueva (verificación de ventas del 27-oct: una Pylontech salía con el costo de la Fortress). */
  z.strictObject({ origen: z.literal('proveedor'), sin_iva: z.number().positive().max(2_000_000), fuente: z.string().trim().min(2).max(200), para: z.string().trim().min(2).max(80) }),
]);

/** «Fortress eFlex MAX 5.4»: con esto se amarra el costo de proveedor a su batería. */
export const nombreBateria = (b: Pick<BateriaAmpliacion, 'marca' | 'modelo'>): string => `${b.marca.trim()} ${b.modelo.trim()}`;

/** Costo de proveedor fuera de lo razonable por kWh nominal (verificación del 27-oct: un dedazo de $1 o de $1,999,999 sacaba el
 *  PDF). Medido en la tabla de precios y en la cotización de Jorge (27-oct-2026): Pylontech FB-L-16 ≈ $2,500/kWh, Growatt AXE
 *  ≈ $4,200/kWh, Fortress eFlex MAX ≈ $5,450/kWh. La banda deja holgura de sobra; fuera de ella es rojo, no amarillo. */
export const COSTO_KWH_RAZONABLE = Object.freeze({ min: 1_000, max: 15_000 });
export type CostoBateriaAmpliacion = z.infer<typeof CostoBateriaAmpliacion>;

/** La hoja 2 sin lo que ya sale de la batería y de cuántas tiene (actuales, kWh, máximo en paralelo). */
export const Hoja2Ampliacion = CapturaAmpliacionBaterias.omit({ actuales: true, kwh_por_bateria: true, max_en_paralelo: true });
export type Hoja2Ampliacion = z.infer<typeof Hoja2Ampliacion>;

/** Lo que el vendedor captura (`contenido.piezas`). null en un campo = todavía no lo captura (el cálculo dice qué falta). */
export const PiezasAmpliacion = z.strictObject({
  bateria: BateriaAmpliacion.nullable(),
  /** Baterías que el cliente YA tiene (del mismo modelo). */
  actuales: z.int().min(1).max(64).nullable(),
  /** Baterías que se suman (la partida). */
  nuevas: z.int().min(1).max(64).nullable(),
  /** Costo de CADA batería, sin IVA. */
  costo_bateria: CostoBateriaAmpliacion.nullable(),
  /** Costo del material eléctrico, sin IVA (0 = no lleva material: no sale su partida). */
  costo_material_sin_iva: z.number().nonnegative().max(2_000_000).nullable(),
  /** Costo de la instalación (mano de obra), sin IVA. */
  costo_instalacion_sin_iva: z.number().nonnegative().max(2_000_000).nullable(),
  /** Viáticos y traslado, sin IVA (van en la partida de instalación). Fuera de Hermosillo son obligatorios. */
  costo_viaticos_sin_iva: z.number().nonnegative().max(2_000_000).nullable(),
  /** El costo de las baterías es de una cotización de proveedor por confirmar: la descripción lo dice al cliente. */
  precio_sujeto_a_confirmacion: z.boolean(),
  anticipo_pct: z.int().min(0).max(100),
  entrega_dias_habiles: z.strictObject({ min: z.int().min(1).max(90), max: z.int().min(1).max(90) }).refine((e) => e.min <= e.max, 'Entrega: el mínimo no puede pasar del máximo.'),
  /** La hoja 2 «cuánto te ayudan las baterías nuevas» (null = sin hoja 2). */
  hoja2: Hoja2Ampliacion.nullable(),
});
export type PiezasAmpliacion = z.infer<typeof PiezasAmpliacion>;

/** Condiciones fijas de una ampliación (Diego, 27-oct noche, ADENDA z43): anticipo 80 % y entrega de 7 a 15 días hábiles, como
 *  la de Jorge. El vendedor no las cambia; si hacen falta otras, las pone Diego (el servidor lo exige). */
export const ANTICIPO_AMPLIACION_PCT = 80;
export const ENTREGA_AMPLIACION_DIAS: Readonly<{ min: number; max: number }> = { min: 7, max: 15 };

/** Una ampliación recién creada: lo de Jorge como punto de partida (anticipo 80 %, entrega de 7 a 15 días hábiles). */
export function piezasAmpliacionVacia(bateria: BateriaAmpliacion | null = null): PiezasAmpliacion {
  return {
    bateria,
    actuales: null,
    nuevas: null,
    costo_bateria: null,
    costo_material_sin_iva: null,
    costo_instalacion_sin_iva: null,
    costo_viaticos_sin_iva: null,
    precio_sujeto_a_confirmacion: false,
    anticipo_pct: ANTICIPO_AMPLIACION_PCT,
    entrega_dias_habiles: { ...ENTREGA_AMPLIACION_DIAS },
    hoja2: null,
  };
}

/** Reserva del fabricante MEDIDA para esa batería (RESERVAS_FABRICANTE), o null si no está medida (la hoja no la menciona). */
export function reservaDeFicha(b: Pick<BateriaAmpliacion, 'marca' | 'modelo'>): number | null {
  return RESERVAS_FABRICANTE.find((r) => r.marca.test(b.marca.trim()) && r.modelo.test(b.modelo))?.pct ?? null;
}

/** La hoja 2 con lo que sale de la batería elegida y de cuántas tiene (para el armado y la revisión), o null si falta algo. */
export function capturaHoja2(p: PiezasAmpliacion): CapturaAmpliacionBaterias | null {
  if (!p.hoja2 || !p.bateria || p.actuales === null) return null;
  return { ...p.hoja2, actuales: p.actuales, kwh_por_bateria: kwhComercial(p.bateria.kwh_nominales_por_modulo), max_en_paralelo: p.bateria.modulos_max };
}

/** La regla de precio vigente para una cotización (la del vendedor de ESA cotización y los imprevistos de Configuración). */
export interface ReglaPrecioAmpliacion {
  margen_pct: number;
  comision_pct: number;
  imprevistos_pct: number;
}

/** Comisión de una ampliación: la del vendedor (su ficha o su rol) + la del aliado de Optimus; si su comisión es 0 (el
 *  director), la de un interno: «lleva comisión siempre». Recibe las dos de usuario.ts para no depender de él aquí. */
export function comisionAmpliacionPct(comisionVendedorPct: number, comisionAliadoPct: number): number {
  return (comisionVendedorPct > 0 ? comisionVendedorPct : COMISION_MINIMA_AMPLIACION_PCT) + comisionAliadoPct;
}

export const PartidaAmpliacion = z.strictObject({
  id: z.enum(['bateria', 'material', 'instalacion']),
  concepto: z.string().min(2).max(46),
  tipo: z.enum(['pieza', 'trabajo']),
  cantidad: z.int().positive(),
  unidad: z.string().min(1).max(8),
  /** SOLO DIRECTOR (la vista lo quita a los demás: en la batería de tabla es el costo de Diego). */
  costo_unitario_sin_iva: z.number().nonnegative().optional(),
  pu_sin_iva: z.number().nonnegative(),
  importe_sin_iva: z.number().nonnegative(),
  equipo: z.strictObject({ grupo: z.literal('baterias'), marca: z.string(), modelo: z.string() }).nullable(),
  garantia: z.strictObject({ quien: z.enum(['fabricante', 'solarix']), meses: z.int().min(1).max(360) }),
});
export type PartidaAmpliacion = z.infer<typeof PartidaAmpliacion>;

/** Lo que calcula el cotizador (`contenido.calculo_piezas`): nunca se edita a mano; lo rehace el servidor en cada cambio.
 *  `sinCostos` es lo que ve quien no es director. */
export const CalculoAmpliacion = z.strictObject({
  /** SOLO DIRECTOR (servidor, 27-oct): con el divisor y el precio de la partida se despeja el costo de Diego de la batería de
   *  tabla, y trae la comisión de la cotización (vista.ts la quita en todos lados): calculoSinCostos la quita. */
  regla: z.strictObject({ margen_pct: z.number(), comision_pct: z.number(), imprevistos_pct: z.number(), divisor: z.number().positive() }).optional(),
  partidas: z.array(PartidaAmpliacion).min(1).max(4),
  titulo: z.string().min(4).max(26),
  descripcion: z.string().min(10).max(150),
  /** SOLO DIRECTOR: costo, comisión, imprevistos, utilidad y margen (la vista los quita a los demás, como en los solares). */
  costo_total_sin_iva: z.number().nonnegative().optional(),
  contado_sin_iva: z.number().positive(),
  iva: z.number().nonnegative(),
  contado_con_iva: z.number().positive(),
  /** Lista con IVA (la de los 3 MSI): contado con IVA ÷ 0.85, como la calcula la plantilla. */
  lista_con_iva: z.number().positive(),
  /** Pago de cada uno de los 3 MSI (lista ÷ 3), o null si la lista pasa del tope de la adenda A0b ($500,000): la plantilla no los
   *  imprime y la pantalla tampoco debe prometerlos (verificación del 27-oct). */
  msi_3: z.number().positive().nullable(),
  comision_sin_iva: z.number().nonnegative().optional(),
  imprevistos_sin_iva: z.number().nonnegative().optional(),
  utilidad_sin_iva: z.number().optional(),
  /** Margen después de comisión e imprevistos (≥ margen_pct de la regla, por el redondeo hacia arriba). */
  margen_pct: z.number().optional(),
  kwh_hoy: z.number().positive(),
  kwh_con: z.number().positive(),
});
export type CalculoAmpliacion = z.infer<typeof CalculoAmpliacion>;

/** Un aviso del cálculo de la ampliación (el servidor le pone id, origen «motor», fecha…). */
export interface AvisoAmpliacion {
  codigo: 'AMPLIACION_INCOMPLETA' | 'AMPLIACION_NOCHE' | 'FALTAN_VIATICOS' | 'OTRO';
  nivel: 'rojo' | 'amarillo' | 'info';
  mensaje: string;
  campo: string;
}

const r2 = (x: number) => Math.round(x * 100) / 100;
const arriba = (x: number) => Math.ceil(x / REDONDEO_PU_AMPLIACION - 1e-9) * REDONDEO_PU_AMPLIACION;
const pesos = (x: number) => `$${x.toLocaleString('en-US', { minimumFractionDigits: x % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const corto = (s: string, max = 600) => (s.length <= max ? s : `${s.slice(0, max - 1)}…`);

/** Concepto de la partida de instalación: con traslado si lleva viáticos («Instalación en San Carlos, con traslado»). */
export function conceptoInstalacion(ciudad: string, conTraslado: boolean): string {
  if (!conTraslado) return 'Instalación y puesta en marcha';
  const c = `Instalación en ${ciudad.trim()}, con traslado`;
  // «Guaymas #3» o «100%…»: la plantilla no deja «#», «%» ni «$» en una partida (verificación del 27-oct)
  return c.length <= 46 && !/[#%$]/.test(c) ? c : 'Instalación, con traslado';
}

/** Calcula la ampliación: lo que falta (rojos que detienen el PDF), los avisos y, si está completa, las partidas y los totales.
 *  `fueraDeHermosillo` lo decide el servidor con la misma regla del motor (municipio de la obra). */
export function calcularAmpliacion(
  p: PiezasAmpliacion,
  regla: ReglaPrecioAmpliacion,
  o: { ciudad: string; fueraDeHermosillo: boolean; fecha: string; /** costo sin IVA de la batería en la tabla vigente (null = no lo trae) */ costoTablaBateria: number | null },
): { calculo: CalculoAmpliacion | null; avisos: AvisoAmpliacion[] } {
  const avisos: AvisoAmpliacion[] = [];
  const falta: string[] = [];
  if (!p.bateria) falta.push('la batería');
  if (p.actuales === null) falta.push('cuántas baterías ya tiene');
  if (p.nuevas === null) falta.push('cuántas se suman');
  if (!p.costo_bateria) falta.push('el costo de cada batería');
  else if (p.costo_bateria.origen === 'tabla' && !(o.costoTablaBateria && o.costoTablaBateria > 0))
    falta.push('el costo de cada batería (la tabla de precios no lo trae para esa batería: tecléalo de la cotización del proveedor)');
  if (p.costo_material_sin_iva === null) falta.push('el costo del material (0 si no lleva)');
  if (p.costo_instalacion_sin_iva === null) falta.push('el costo de la instalación');
  if (falta.length) avisos.push({ codigo: 'AMPLIACION_INCOMPLETA', nivel: 'rojo', mensaje: `Ampliación de baterías: falta ${falta.join(', ')}. Sin eso no hay precio ni PDF.`, campo: '/piezas' });
  const viaticos = p.costo_viaticos_sin_iva ?? 0;
  if (o.fueraDeHermosillo && !(viaticos > 0))
    avisos.push({ codigo: 'FALTAN_VIATICOS', nivel: 'rojo', mensaje: `Proyecto fuera de Hermosillo (${o.ciudad}): captura los viáticos y el traslado (pregúntale al equipo técnico cuánto salen). Sin viáticos la propuesta no sale.`, campo: '/piezas/costo_viaticos_sin_iva' });
  if (p.bateria && p.actuales !== null && p.nuevas !== null && p.actuales + p.nuevas > p.bateria.modulos_max)
    avisos.push({ codigo: 'AMPLIACION_INCOMPLETA', nivel: 'rojo', mensaje: `Con ${p.actuales} que ya tiene y ${p.nuevas} nuevas son ${p.actuales + p.nuevas} baterías: la ficha de la ${p.bateria.marca} ${p.bateria.modelo} permite ${p.bateria.modulos_max} en paralelo.`, campo: '/piezas/nuevas' });
  // costo de proveedor: de ESTA batería, razonable por kWh y nunca debajo del de la tabla (que es de Diego)
  let proveedorVale = p.costo_bateria?.origen === 'proveedor';
  if (p.costo_bateria?.origen === 'proveedor' && p.bateria) {
    const cb = p.costo_bateria;
    const porKwh = cb.sin_iva / p.bateria.kwh_nominales_por_modulo;
    if (cb.para !== nombreBateria(p.bateria)) {
      proveedorVale = false;
      avisos.push({ codigo: 'AMPLIACION_INCOMPLETA', nivel: 'rojo', mensaje: corto(`El costo de proveedor que está capturado es de la ${cb.para} y la batería elegida es la ${nombreBateria(p.bateria)}: teclea el costo de esta batería (o elige «de la tabla»).`), campo: '/piezas/costo_bateria' });
    } else if (porKwh < COSTO_KWH_RAZONABLE.min || porKwh > COSTO_KWH_RAZONABLE.max) {
      proveedorVale = false;
      avisos.push({ codigo: 'AMPLIACION_INCOMPLETA', nivel: 'rojo', mensaje: corto(`El costo de proveedor (${pesos(cb.sin_iva)} sin IVA por batería) da ${pesos(Math.round(porKwh))} por kWh: fuera de lo razonable (de ${pesos(COSTO_KWH_RAZONABLE.min)} a ${pesos(COSTO_KWH_RAZONABLE.max)} por kWh). ¿Un dedazo? Revisa la cotización del proveedor.`), campo: '/piezas/costo_bateria' });
    } else if (o.costoTablaBateria && cb.sin_iva < o.costoTablaBateria) {
      avisos.push({ codigo: 'OTRO', nivel: 'amarillo', mensaje: 'El costo de proveedor es menor que el de la tabla de precios de Diego: el precio se calcula con el de la tabla (para cotizar con un costo menor, Diego actualiza la tabla).', campo: '/piezas/costo_bateria' });
    } else
      avisos.push({ codigo: 'OTRO', nivel: 'amarillo', mensaje: corto(`Costo de la batería tecleado (no viene de la tabla de precios): ${pesos(cb.sin_iva)} sin IVA por batería, de «${cb.fuente}». Diego lo ve en la bitácora al generar el PDF.`), campo: '/piezas/costo_bateria' });
  }
  if (p.costo_instalacion_sin_iva === 0)
    avisos.push({ codigo: 'OTRO', nivel: 'amarillo', mensaje: 'Sin costo de instalación: el PDF dice «Te entregamos tus piezas» y no cobra mano de obra. Si la instalación va incluida, captura su costo.', campo: '/piezas/costo_instalacion_sin_iva' });
  // la reserva del fabricante sale de la ficha (no se teclea): «es la reserva que recomienda el fabricante» solo con la medida
  if (p.hoja2 && p.bateria) {
    const ficha = reservaDeFicha(p.bateria);
    if (p.hoja2.reserva_fabricante_pct !== ficha)
      avisos.push({ codigo: 'AMPLIACION_NOCHE', nivel: 'rojo', mensaje: ficha === null ? `La ${nombreBateria(p.bateria)} no trae una reserva del fabricante medida: deja ese dato vacío (la hoja no la menciona).` : `La reserva que recomienda el fabricante de la ${nombreBateria(p.bateria)} es ${ficha} % (de su manual): ese dato no se teclea.`, campo: '/piezas/hoja2/reserva_fabricante_pct' });
    else if (ficha !== null && p.hoja2.corte_pct < ficha)
      avisos.push({ codigo: 'OTRO', nivel: 'amarillo', mensaje: `El corte del inversor (${p.hoja2.corte_pct} %) es MENOR que la reserva que recomienda el fabricante (${ficha} %): la hoja 2 cuenta la noche hasta el ${p.hoja2.corte_pct} % y no dice «cuidamos tus baterías». Conviene subir el corte antes de prometer.`, campo: '/piezas/hoja2/corte_pct' });
  }
  const h2 = capturaHoja2(p);
  if (p.hoja2 && h2 && p.nuevas !== null) {
    const errores = revisarAmpliacionBaterias(h2, { nuevas: p.nuevas, fecha: o.fecha });
    if (errores.length) avisos.push({ codigo: 'AMPLIACION_NOCHE', nivel: 'rojo', mensaje: corto(`Hoja 2 (la noche medida): ${errores.join(' ')}`), campo: '/piezas/hoja2' });
  }
  const divisor = 1 - (regla.margen_pct + regla.comision_pct + regla.imprevistos_pct) / 100;
  if (!(divisor > 0.05)) {
    avisos.push({ codigo: 'AMPLIACION_INCOMPLETA', nivel: 'rojo', mensaje: 'La regla de precio no da: el margen, la comisión y los imprevistos se comen el precio. Avísale a Diego (sin precio no hay PDF).', campo: '/piezas' });
    return { calculo: null, avisos };
  }
  const costoBateria = p.costo_bateria?.origen === 'proveedor' ? (proveedorVale ? Math.max(p.costo_bateria.sin_iva, o.costoTablaBateria ?? 0) : null) : o.costoTablaBateria;
  if (falta.length || !p.bateria || p.actuales === null || p.nuevas === null || !costoBateria || p.costo_material_sin_iva === null || p.costo_instalacion_sin_iva === null) return { calculo: null, avisos };

  const kwh = kwhComercial(p.bateria.kwh_nominales_por_modulo);
  const partida = (id: PartidaAmpliacion['id'], concepto: string, tipo: PartidaAmpliacion['tipo'], cantidad: number, unidad: string, costoUnit: number, garantia: PartidaAmpliacion['garantia'], equipo: PartidaAmpliacion['equipo'] = null): PartidaAmpliacion => {
    const pu = arriba(costoUnit / divisor);
    return { id, concepto, tipo, cantidad, unidad, costo_unitario_sin_iva: r2(costoUnit), pu_sin_iva: pu, importe_sin_iva: r2(pu * cantidad), equipo, garantia };
  };
  const partidas: PartidaAmpliacion[] = [
    partida('bateria', conceptoBateria(p.bateria, kwh), 'pieza', p.nuevas, 'pza', costoBateria, { quien: 'fabricante', meses: p.bateria.garantia_anios * 12 }, { grupo: 'baterias', marca: p.bateria.marca, modelo: p.bateria.modelo }),
  ];
  if (p.costo_material_sin_iva > 0) partidas.push(partida('material', 'Material eléctrico para conectar las baterías', 'pieza', 1, 'lote', p.costo_material_sin_iva, { quien: 'solarix', meses: 12 }));
  const instalacion = p.costo_instalacion_sin_iva + viaticos;
  if (instalacion > 0) partidas.push(partida('instalacion', conceptoInstalacion(o.ciudad, viaticos > 0), 'trabajo', 1, 'servicio', instalacion, { quien: 'solarix', meses: 12 }));
  const contado = r2(partidas.reduce((s, x) => s + x.importe_sin_iva, 0));
  const costo = r2(costoBateria * p.nuevas + p.costo_material_sin_iva + instalacion);
  const comision = r2((contado * regla.comision_pct) / 100);
  const imprevistos = r2((contado * regla.imprevistos_pct) / 100);
  const utilidad = r2(contado - costo - comision - imprevistos);
  const iva = r2(contado * IVA_PIEZAS);
  const conIva = r2(contado + iva);
  let descripcion = descripcionAmpliacion({ marca: p.bateria.marca, modelo: p.bateria.modelo, actuales: p.actuales, nuevas: p.nuevas, kwh, conHoja2: !!p.hoja2 });
  const sujeto = ' Precio de las baterías sujeto a confirmación del proveedor.';
  if (p.precio_sujeto_a_confirmacion) {
    if (descripcion.length + sujeto.length <= 150) descripcion += sujeto;
    else avisos.push({ codigo: 'OTRO', nivel: 'amarillo', mensaje: 'La frase «precio sujeto a confirmación del proveedor» no cabe en la descripción: díselo al cliente al mandarle la propuesta.', campo: '/piezas/precio_sujeto_a_confirmacion' });
  }
  return {
    calculo: {
      regla: { margen_pct: regla.margen_pct, comision_pct: regla.comision_pct, imprevistos_pct: regla.imprevistos_pct, divisor: Math.round(divisor * 1e6) / 1e6 },
      partidas,
      titulo: 'Ampliación de baterías',
      descripcion,
      costo_total_sin_iva: costo,
      contado_sin_iva: contado,
      iva,
      contado_con_iva: conIva,
      // como la plantilla (piezas/modelo.mjs: lista = Math.round(total ÷ 0.85), y los 3 MSI = lista ÷ 3): pantalla y PDF iguales al centavo
      lista_con_iva: Math.round(conIva / 0.85),
      msi_3: Math.round(conIva / 0.85) <= MSI_TOPE_LISTA_CON_IVA ? r2(Math.round(conIva / 0.85) / 3) : null,
      comision_sin_iva: comision,
      imprevistos_sin_iva: imprevistos,
      utilidad_sin_iva: utilidad,
      margen_pct: Math.round((utilidad / contado) * 10000) / 100,
      kwh_hoy: r2(p.actuales * kwh),
      kwh_con: r2((p.actuales + p.nuevas) * kwh),
    },
    avisos,
  };
}

/** Lo que ve quien no es director del cálculo de una ampliación: sin costo, comisión, imprevistos, utilidad, margen ni la
 *  regla (el costo de la batería de tabla es de Diego: con el divisor se despejaba). La usan vistaSinCostos (vista.ts: toda
 *  respuesta del servidor) y el servidor simulado. */
export function calculoSinCostos(c: CalculoAmpliacion): CalculoAmpliacion {
  const { costo_total_sin_iva: _c, comision_sin_iva: _k, imprevistos_sin_iva: _i, utilidad_sin_iva: _u, margen_pct: _m, regla: _r, ...resto } = c;
  return { ...resto, partidas: c.partidas.map(({ costo_unitario_sin_iva: _x, ...p }) => p) };
}

